import { Status } from '@mm/components';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as KlaviyoOrdersIcon } from 'assets/icons/klaviyo_orders.svg';
import { ReactComponent as KlaviyoRevenueIcon } from 'assets/icons/klaviyo_revenue.svg';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Spinner from '../../../../../../../../common/components/Spinner';
import styles from './KlaviyoIntegrations.module.css';

type Props = {
  facebookPageId: number;
  getRevenues: (pageId: number, force: boolean) => void;
};

const revenueRanges: [number, string][] = [
  [1, ''],
  [1e3, ' K'],
  [1e6, ' M'],
  [1e9, ' B'],
  [1e12, ' T'],
];

const formattedRevenueValue = (revenue: number) => {
  let matchingRangeIndex = -1;
  for (const range of revenueRanges) {
    if (range[0] < revenue) {
      matchingRangeIndex += 1;
    } else {
      break;
    }
  }
  matchingRangeIndex = Math.min(
    Math.max(matchingRangeIndex, 0),
    revenueRanges.length - 1,
  );
  const range = revenueRanges[matchingRangeIndex];
  const fixedValue = (revenue / range[0]).toFixed(1);

  return fixedValue + range[1];
};

export const KlaviyoRevenue = ({ facebookPageId, getRevenues }: Props) => {
  const revenue = useSelector((state: StoreState) => state.klaviyo.revenue);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      await getRevenues(facebookPageId, false);
      setIsLoading(false);
    })();
  }, []);

  return (
    <div className={styles.revenueContainer}>
      <div className={styles.revenueBoxes}>
        <div className={styles.revenueBox}>
          {isLoading && (
            <div className={styles.revenueSpinnerWrapper}>
              <Spinner
                show={true}
                position="absolute"
                backgroundColor="none"
                size={30}
              />
            </div>
          )}
          <KlaviyoRevenueIcon />
          <div className={styles.revenueData}>
            <span className={styles.revenueDataLabel}>
              Customers.ai Contact Purchase Value
            </span>
            <span className={styles.revenueDataValue}>
              {revenue.totalRevenue === 0.0 ? (
                <Status
                  status="Purchase value will appear here"
                  color="#1890FF"
                  className={styles.revenueStatus}
                />
              ) : (
                `$ ${formattedRevenueValue(revenue.totalRevenue)}`
              )}
            </span>
          </div>
        </div>
        <div className={styles.revenueBox}>
          {isLoading && (
            <div className={styles.revenueSpinnerWrapper}>
              <Spinner
                show={true}
                position="absolute"
                backgroundColor="none"
                size={30}
              />
            </div>
          )}
          <KlaviyoOrdersIcon />
          <div className={styles.revenueData}>
            <span className={styles.revenueDataLabel}>Number of Purchases</span>
            <span className={styles.revenueDataValue}>
              {revenue.totalOrders === 0 ? (
                <Status
                  status="Purchases will appear here"
                  color="#1890FF"
                  className={styles.revenueStatus}
                />
              ) : (
                new Intl.NumberFormat().format(revenue.totalOrders)
              )}
            </span>
          </div>
        </div>
      </div>
      <div className={styles.revenueSubtext}>
        Revenue is calculated by checking the email contacts in our system
        against the orders in your Klaviyo account.
      </div>
    </div>
  );
};
